// ================================================================
// 命盤分享公開瀏覽頁（無需登入）
// ================================================================

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button,
  Alert, CircularProgress, Chip, Grid,
  InputAdornment, IconButton,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Lock as LockIcon,
  AutoAwesome as AutoAwesomeIcon, PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { accessShare, ShareSnapshot, ShareField, SharePurpose, SHARE_PURPOSE_OPTIONS } from '../api/share';
import { isAuthenticated } from '../api/client';

// ================================================================
// 命盤格局圖（與 ChartPage 相同邏輯）
// ================================================================

const DIZHI_NAMES = ['寅','卯','辰','巳','午','未','申','酉','戌','亥','子','丑'];

// 4×4 格局：每個格子的 {row, col}（左上為 (0,0)，CSS grid 1-based）
const GRID_PLACEMENT: Record<number, { row: number; col: number }> = {
  4:  { row: 1, col: 1 },  5:  { row: 1, col: 2 },
  6:  { row: 1, col: 3 },  7:  { row: 1, col: 4 },
  8:  { row: 2, col: 4 },  9:  { row: 3, col: 4 },
  10: { row: 4, col: 4 }, 11:  { row: 4, col: 3 },
  12: { row: 4, col: 2 },  1:  { row: 4, col: 1 },
  2:  { row: 3, col: 1 },  3:  { row: 2, col: 1 },
};

const buildChartData = (rawData: any) => {
  if (!rawData || !Array.isArray(rawData)) return null;
  const starsInfo = rawData[5] as any[];
  const twelveGrids = rawData[6] as any[];
  const imageYearsOld = rawData[2] as number;
  const otherInfo = rawData[18] as any[];
  const inputTimeLabel = rawData[0];
  const genderCode = rawData[1] as number;

  const mingGongItem = twelveGrids?.find((g: any) => g.Purple_SquareReOrder_MinGon === '命宮');
  const mingPos: number = mingGongItem?.TwelveGridsNumber || rawData[7]?.[0]?.[0] || 1;

  const shenGongStar = starsInfo?.find((s: any) => s.StarsChineseName === '身宮');
  const shenPos: number = shenGongStar?.Result_StarPosition || rawData[7]?.[1]?.[0] || 1;

  const getStars = (pos: number) => {
    const result = { main: [] as string[], aux: [] as string[], sihua: [] as string[], zayao: [] as string[] };
    if (!starsInfo) return result;
    for (const star of starsInfo) {
      if (star.Result_StarPosition !== pos) continue;
      const name: string = star.StarsChineseName;
      if (star.StarsLevel === 1) result.main.push(name);
      else if (star.StarsLevel === 2) result.aux.push(name);
      else if (star.StarsLevel === 4) result.sihua.push(name);
      else if (star.StarsLevel === 3) result.zayao.push(name);
    }
    return result;
  };

  const birthTimeStr = String(inputTimeLabel);
  const birthYear = birthTimeStr.substring(0, 4);   // 原始年份，大限 CE 計算用
  const hourCode = birthTimeStr.substring(8, 10);
  const hourMap: Record<string, string> = {
    '23': '子時', '01': '丑時', '03': '寅時', '05': '卯時',
    '07': '辰時', '09': '巳時', '11': '午時', '13': '未時',
    '15': '申時', '17': '酉時', '19': '戌時', '21': '亥時',
  };

  // 顯示用日期：子時（hh=23）命盤計算日 = 儲存日 +1 天（0_PreLoad.r line 359）
  let dispY = parseInt(birthTimeStr.substring(0, 4), 10);
  let dispM = parseInt(birthTimeStr.substring(4, 6), 10);
  let dispD = parseInt(birthTimeStr.substring(6, 8), 10);
  if (hourCode === '23') {
    const dt = new Date(dispY, dispM - 1, dispD);
    dt.setDate(dt.getDate() + 1);
    dispY = dt.getFullYear(); dispM = dt.getMonth() + 1; dispD = dt.getDate();
  }
  const birthLabel = `${dispY}年 ${dispM}月 ${dispD}日 ${hourMap[hourCode] || hourCode}`;
  const genderLabel = genderCode === 1 ? '男命' : '女命';
  const nayin = Array.isArray(otherInfo) ? String(otherInfo[3] ?? '') : '';

  // 目前大限
  let currentBigLimit: { name: string; start: number; end: number; birthYearCE: number } | null = null;
  if (twelveGrids && imageYearsOld) {
    const virtualAge = Number(imageYearsOld);
    const birthYearNum = parseInt(birthYear, 10);
    for (const grid of twelveGrids) {
      const raw = String(grid.BigLimitYear_MingonReOrderList ?? '');
      const m = raw.match(/(\d+)歲至(\d+)歲/);
      if (m) {
        const s = parseInt(m[1], 10), e = parseInt(m[2], 10);
        if (virtualAge >= s && virtualAge <= e) {
          currentBigLimit = { name: String(grid.Purple_SquareReOrder_MinGon), start: s, end: e, birthYearCE: birthYearNum };
          break;
        }
      }
    }
  }

  return { twelveGrids, mingPos, shenPos, getStars, birthLabel, genderLabel, nayin, currentBigLimit, imageYearsOld };
};

// 命盤格局圖元件
const ChartGrid: React.FC<{ rawData: any; nickname?: string }> = ({ rawData, nickname }) => {
  const cd = buildChartData(rawData);
  if (!cd) return <Alert severity="warning">命盤資料無法顯示</Alert>;
  const { twelveGrids, mingPos, shenPos, getStars, birthLabel, genderLabel, nayin } = cd;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(4, auto)',
        gap: '1px',
        backgroundColor: '#aaa',
        minWidth: 560,
        border: '1px solid #aaa',
        borderRadius: 1,
      }}>
        {Array.from({ length: 12 }, (_, i) => {
          const pos = i + 1;
          const pl = GRID_PLACEMENT[pos];
          if (!pl) return null;
          const gongItem = twelveGrids?.find((g: any) => g.TwelveGridsNumber === pos);
          const funcName = String(gongItem?.Purple_SquareReOrder_MinGon ?? '');
          const ganZhi = String(gongItem?.Purple_Monthganzi_list ?? '');
          const bigLimitAge = gongItem?.BigLimitYear_MingonReOrderList != null
            ? Number(gongItem.BigLimitYear_MingonReOrderList) : null;
          const isMing = pos === mingPos;
          const isShen = pos === shenPos;
          const stars = getStars(pos);

          return (
            <Box key={pos} sx={{
              gridColumn: pl.col, gridRow: pl.row,
              backgroundColor: isMing ? '#FCE4EC' : isShen ? '#E8EAF6' : 'white',
              p: '6px 4px',
              minHeight: 120,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>{ganZhi}</Typography>
                {bigLimitAge !== null && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
                    {bigLimitAge}~{bigLimitAge + 9}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 'bold', color: '#c00', textAlign: 'center', mb: 0.3 }}>
                {funcName}
                {isMing && <Chip label="命" size="small" sx={{ ml: 0.3, height: 14, fontSize: '0.55rem', bgcolor: '#e91e63', color: 'white' }} />}
                {isShen && <Chip label="身" size="small" sx={{ ml: 0.3, height: 14, fontSize: '0.55rem', bgcolor: '#5c6bc0', color: 'white' }} />}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.4 }}>
                {stars.main.join(' ')}
              </Typography>
              {stars.sihua.length > 0 && (
                <Typography sx={{ fontSize: '0.65rem', color: '#1565c0', textAlign: 'center' }}>
                  {stars.sihua.join(' ')}
                </Typography>
              )}
              {stars.aux.length > 0 && (
                <Typography sx={{ fontSize: '0.62rem', color: '#4a148c', textAlign: 'center' }}>
                  {stars.aux.join(' ')}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* 中央 2×2 */}
        <Box sx={{
          gridColumn: '2 / 4', gridRow: '2 / 4',
          backgroundColor: '#f9f0ff',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          p: 1, gap: 0.5,
        }}>
          <AutoAwesomeIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
          {nickname && (
            <Typography variant="caption" fontWeight="bold" color="primary">{nickname}</Typography>
          )}
          <Typography sx={{ fontSize: '0.7rem', color: '#555', textAlign: 'center' }}>
            {birthLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>{genderLabel}</Typography>
          {nayin && (
            <Typography sx={{ fontSize: '0.65rem', color: '#888' }}>納音：{nayin}</Typography>
          )}
          <Typography sx={{ fontSize: '0.6rem', color: '#aaa', mt: 0.5, textAlign: 'center' }}>
            命宮：{DIZHI_NAMES[mingPos - 1]}宮
          </Typography>
        </Box>
      </Box>

      {/* 圖例 */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: '#FCE4EC', border: '1px solid #ccc' }} />
          <Typography variant="caption">命宮</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: '#E8EAF6', border: '1px solid #ccc' }} />
          <Typography variant="caption">身宮</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ================================================================
// 主頁面
// ================================================================

// 分享目的 → 預設配對問題
const PAIR_PURPOSE_QUESTION: Record<string, { id: string; text: string; price: number }> = {
  romantic:   { id: 'PAIR001', text: '我目前交往的對象適合我嗎?',    price: 199 },
  friendship: { id: 'PAIR007', text: '我們的價值觀一致嗎?',          price: 199 },
  career:     { id: 'PAIR016', text: '這個人適合當我的工作夥伴嗎?',  price: 199 },
};

const ShareViewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<ShareSnapshot | null>(null);
  const [sharedFields, setSharedFields] = useState<ShareField[]>([]);
  const [sharePurpose, setSharePurpose] = useState<SharePurpose>('personal');

  const isLoggedIn = isAuthenticated();

  const handlePairCTA = () => {
    if (!snapshot?.chart_raw) return;
    const bt = String(snapshot.chart_raw[0] ?? '');
    if (bt.length >= 8) {
      const year  = bt.substring(0, 4);
      const month = String(parseInt(bt.substring(4, 6), 10));
      const day   = String(parseInt(bt.substring(6, 8), 10));
      const hh    = bt.substring(8, 10);
      // hh='23' 可能是 23_early 或 23_late，預設 23_late（使用者可自行修正）
      const hour  = hh === '23' ? '23_late' : hh;
      const gender: 'Male' | 'Female' = (snapshot.chart_raw[1] as number) === 1 ? 'Male' : 'Female';

      sessionStorage.setItem('pair_from_share', JSON.stringify({
        year, month, day, hour, gender, name: snapshot.nickname ?? '',
        share_token: token ?? '',
      }));

      const q = PAIR_PURPOSE_QUESTION[sharePurpose];
      if (q) {
        sessionStorage.setItem('pairAnalysisQuestion', JSON.stringify({
          question_id: q.id, question_text: q.text, price: q.price,
        }));
      }
    }

    if (isLoggedIn) {
      navigate('/pair');
    } else {
      navigate('/login', { state: { from: { pathname: '/pair' } } });
    }
  };

  const handleAccess = async () => {
    if (!token || !password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await accessShare(token, password);
      if (res.success && res.data) {
        setSnapshot(res.data);
        setSharedFields(res.shared_fields ?? []);
        setSharePurpose(res.share_purpose ?? 'personal');
      } else {
        if (res.error === 'wrong_password') setError('密碼錯誤，請再試一次');
        else if (res.error === 'not_found') setError('此分享連結不存在');
        else if (res.error === 'revoked') setError('此分享連結已被撤銷');
        else setError('無法存取，請稍後再試');
      }
    } catch {
      setError('網路錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 密碼輸入頁
  if (!snapshot) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 2,
      }}>
        <Paper elevation={6} sx={{ p: 4, maxWidth: 400, width: '100%', borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>查看命盤</Typography>
            <Typography variant="body2" color="text.secondary">
              朋友分享了他的紫微斗數命盤，請輸入查看密碼
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="查看密碼"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAccess()}
            autoFocus
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleAccess}
            disabled={loading || !password}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            查看命盤
          </Button>
        </Paper>
      </Box>
    );
  }

  // 命盤顯示頁
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4,
    }}>
      <Container maxWidth="md">
        {/* 標題 */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'white', mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
            紫微斗數命盤
          </Typography>
          {snapshot.nickname && (
            <Typography variant="h6" color="rgba(255,255,255,0.85)">
              {snapshot.nickname} 的命盤
            </Typography>
          )}
        </Box>

        <Paper elevation={4} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }}>
          {/* 基本資訊 */}
          {(sharedFields.includes('birth_year') || sharedFields.includes('gender')) && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              {snapshot.birth_year && sharedFields.includes('birth_year') && (
                <Chip label={`${snapshot.birth_year} 年`} color="primary" variant="outlined" size="small" />
              )}
              {snapshot.gender_label && sharedFields.includes('gender') && (
                <Chip label={snapshot.gender_label} color="secondary" variant="outlined" size="small" />
              )}
            </Box>
          )}

          {/* 命宮主星 */}
          {snapshot.mingong_star && sharedFields.includes('mingong_star') && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fce4ec', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>命宮主星</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {snapshot.mingong_star}
              </Typography>
            </Box>
          )}

          {/* 目前大限 */}
          {snapshot.current_big_limit && sharedFields.includes('big_limit') && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8eaf6', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>目前大限</Typography>
              <Typography variant="body1" fontWeight="bold">
                {snapshot.current_big_limit.palace_name}（{snapshot.current_big_limit.start_age}～{snapshot.current_big_limit.end_age} 歲）
              </Typography>
            </Box>
          )}

          {/* 命盤格局圖 */}
          {sharedFields.includes('chart_grid') && snapshot.chart_raw && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>命盤格局圖</Typography>
              <ChartGrid rawData={snapshot.chart_raw} nickname={snapshot.nickname} />
            </Box>
          )}
        </Paper>

        {/* CTA */}
        {sharePurpose !== 'personal' ? (
          <Paper elevation={4} sx={{ p: 3, borderRadius: 3, textAlign: 'center', background: 'rgba(255,255,255,0.97)' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {SHARE_PURPOSE_OPTIONS.find(o => o.key === sharePurpose)?.icon}{' '}
              {SHARE_PURPOSE_OPTIONS.find(o => o.key === sharePurpose)?.ctaLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isLoggedIn
                ? `點擊後將自動預填${snapshot?.nickname ? `「${snapshot.nickname}」` : '對方'}的資料，直接進行配對分析`
                : '登入後即可與對方進行配對分析；新使用者註冊即獲贈 499 點'}
            </Typography>
            <Grid container spacing={1} justifyContent="center">
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonAddIcon />}
                  onClick={handlePairCTA}
                  disabled={!snapshot?.chart_raw}
                  sx={{ background: 'linear-gradient(135deg, #e91e63, #9c27b0)' }}
                >
                  {isLoggedIn ? '立即配對分析' : '登入後配對分析'}
                </Button>
              </Grid>
              {!isLoggedIn && (
                <Grid item>
                  <Button variant="outlined" onClick={() => navigate('/register')}>
                    新使用者？免費註冊
                  </Button>
                </Grid>
              )}
            </Grid>
            {!snapshot?.chart_raw && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                （對方未分享命盤格局圖，無法直接配對）
              </Typography>
            )}
          </Paper>
        ) : (
          <Paper elevation={4} sx={{ p: 3, borderRadius: 3, textAlign: 'center', background: 'rgba(255,255,255,0.95)' }}>
            <AutoAwesomeIcon sx={{ color: '#9c27b0', fontSize: 32, mb: 1 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>想了解自己的命盤？</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              免費試算紫微斗數命盤，解析你的個性、運勢與人生方向
            </Typography>
            <Grid container spacing={1} justifyContent="center">
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate('/register')}
                  sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
                >
                  免費註冊試算
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={() => navigate('/login')}>
                  已有帳號？登入
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ShareViewPage;
